import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { industries } from '../mock/mockData';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';

/**
 * FeaturedIndustriesCarousel — auto-rotating carousel surfacing all 14
 * industries below the homepage hero. Responsive (1/2/3/4 cards visible),
 * pauses on hover, supports keyboard arrow navigation and dots.
 */

const ROTATE_MS = 4000;
const BREAKPOINTS = [
  { min: 0,    perView: 1 },
  { min: 640,  perView: 2 },
  { min: 1024, perView: 3 },
  { min: 1280, perView: 4 },
];

const getPerView = () => {
  if (typeof window === 'undefined') return 4;
  const w = window.innerWidth;
  return BREAKPOINTS.reduce((acc, b) => (w >= b.min ? b.perView : acc), 1);
};

const FeaturedIndustriesCarousel = () => {
  const { tt } = useLang();
  const [perView, setPerView] = useState(getPerView());
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  const maxIndex = Math.max(0, industries.length - perView);

  useEffect(() => {
    const onResize = () => setPerView(getPerView());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1 > maxIndex ? 0 : i + 1));
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, maxIndex]);

  const go = (delta) => {
    setIndex(i => {
      const next = i + delta;
      if (next < 0) return maxIndex;
      if (next > maxIndex) return 0;
      return next;
    });
  };

  const onKey = (e) => {
    if (e.key === 'ArrowLeft') go(-1);
    else if (e.key === 'ArrowRight') go(1);
  };

  const cardWidth = 100 / perView;
  const offset = index * cardWidth;

  const trackClick = (ind) => {
    let sessionId = '';
    try { sessionId = sessionStorage.getItem('ld_session') || ''; } catch { /* ignore */ }
    api.trackIndustryClick({ slug: ind.slug, title: ind.title, page: '/', session_id: sessionId });
  };

  return (
    <section
      data-testid="featured-industries-carousel"
      className="relative bg-[#0b0f14] text-white overflow-hidden py-16 md:py-20 border-y border-gray-900/40"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKey}
      tabIndex={0}
      aria-label="Featured industries carousel"
    >
      {/* decorative background */}
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_30%,rgba(249,115,22,0.10),transparent_60%)] pointer-events-none"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_70%,rgba(148,163,184,0.12),transparent_55%)] pointer-events-none"></div>

      <div className="relative max-w-[1400px] mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-8 md:mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-orange-400 text-[10px] uppercase tracking-[0.25em] font-bold mb-2">
              <Icons.Sparkles className="w-3.5 h-3.5" /> {tt('Featured')}
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-black leading-tight">
              {tt('Industries we')} <span className="text-green-500">{tt('serve')}</span>
            </h2>
            <p className="hidden md:block text-gray-400 mt-3 max-w-2xl text-sm leading-relaxed">
              {tt('From smart cities to oil & gas — explore the 14 verticals where Land Drafters delivers engineering depth across 40+ countries.')}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous industries"
              data-testid="featured-industries-prev"
              className="w-10 h-10 rounded-full border border-gray-700/60 hover:border-green-400 hover:bg-gray-900/40 text-white flex items-center justify-center transition-colors"
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next industries"
              data-testid="featured-industries-next"
              className="w-10 h-10 rounded-full border border-gray-700/60 hover:border-green-400 hover:bg-gray-900/40 text-white flex items-center justify-center transition-colors"
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </button>
            <Link
              to="/industries"
              data-testid="featured-industries-explore"
              className="hidden sm:inline-flex items-center gap-2 ml-2 text-[11px] font-bold uppercase tracking-wider text-orange-400 hover:text-green-400 transition-colors"
            >
              {tt('Explore all')} <Icons.ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* track */}
        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${offset}%)` }}
          >
            {industries.map(ind => {
              const Ic = Icons[ind.icon] || Icons.Briefcase;
              return (
                <div
                  key={ind.slug}
                  className="shrink-0 px-2 md:px-3"
                  style={{ width: `${cardWidth}%` }}
                >
                  <Link
                    to={`/industries/${ind.slug}`}
                    data-testid={`featured-ind-${ind.slug}`}
                    onClick={() => trackClick(ind)}
                    className="group block h-full bg-gradient-to-br from-[#1d2128] to-[#161a20] hover:from-gray-900/60 hover:to-[#161a20] border border-gray-800/50 hover:border-green-400/80 rounded-xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-gray-600/20"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-gray-700/30 group-hover:bg-orange-500 border border-gray-600/50 group-hover:border-orange-400 flex items-center justify-center transition-colors">
                        <Ic className="w-6 h-6 text-green-300 group-hover:text-white transition-colors" />
                      </div>
                      <Icons.ArrowUpRight className="w-5 h-5 text-gray-500 group-hover:text-orange-400 group-hover:rotate-12 transition-all" />
                    </div>
                    <h3 className="font-black text-white text-lg leading-tight group-hover:text-green-300 transition-colors">
                      {tt(ind.title)}
                    </h3>
                    <p className="text-[12px] text-gray-400 mt-3 leading-relaxed line-clamp-3">
                      {tt(ind.desc)}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-4 text-[10px] font-bold uppercase tracking-wider text-green-400 group-hover:text-orange-400 transition-colors">
                      {tt('Explore sector')} <Icons.ArrowRight className="w-3 h-3" />
                    </span>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* dots */}
        <div className="flex items-center justify-center gap-1.5 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={`dot-${i}-of-${maxIndex}`}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`featured-industries-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-8 bg-orange-500' : 'w-3 bg-gray-900/60 hover:bg-gray-700/80'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedIndustriesCarousel;
